/**
 * StworzCreator — publiczny kreator „pytanie-odpowiedź" (Faza 2, styl Von Halsky).
 * Potencjalny klient sam generuje swoją stronę: kilka pytań → gotowa strona na żywo (WOW)
 * → „Załóż konto (e-mail), żeby zapisać i opublikować".
 *
 * Zasada naczelna: NA KAŻDYM kroku i na ekranie „gotowe" komunikujemy, że to dopiero
 * początek — resztę (a dla HODOWCY: psy/reproduktory/suki i mioty) uzupełni w panelu.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import { DOG_BREEDS } from '@/data/breeds';
import { BUSINESS_INDUSTRIES } from '@/pages/BusinessWizardV2/industries';
import { provisionFromDraft } from '@/utils/provisionPage';
import { PageShareBar } from '@/components/SocialShare';
import {
  Dog, Building2, ArrowRight, ArrowLeft, Sparkles, Check, Search,
  PartyPopper, PawPrint, ImagePlus, Phone, Mail, MapPin, Loader2, X,
} from 'lucide-react';

const ACCENT = '#F97316';
const MASCOT = '/mascot-user-dog.png';

const ORG_OPTIONS = ['ZKwP (FCI)', 'KHRON', 'WUSH', 'Inna organizacja', 'Jeszcze nie'];

const STEPS = {
  hodowla: [
    { key: 'name', q: 'Jak nazywa się Twoja hodowla?', sub: 'Ta nazwa pojawi się na górze Twojej strony.', required: true },
    { key: 'breeds', q: 'Jaką rasę (lub rasy) hodujesz?', sub: 'Wybierz jedną lub więcej.', required: true },
    { key: 'city', q: 'W jakim mieście działasz?', sub: 'Dzięki temu klienci z okolicy Cię znajdą.', required: true },
    { key: 'organization', q: 'Czy jesteś zrzeszony w związku?', sub: 'Możesz też wybrać „Jeszcze nie".', required: false },
    { key: 'photo', q: 'Dodaj główne zdjęcie', sub: 'Możesz dodać teraz albo później w panelu.', required: false },
    { key: 'description', q: 'Napisz kilka słów o sobie', sub: 'Albo pozwól, że napiszę za Ciebie.', required: false },
    { key: 'contact', q: 'Jak klienci mają się z Tobą kontaktować?', sub: 'Wystarczy telefon lub e-mail.', required: false },
  ],
  biznes: [
    { key: 'name', q: 'Jak nazywa się Twoja firma?', sub: 'Ta nazwa pojawi się na górze wizytówki.', required: true },
    { key: 'industry', q: 'Czym się zajmujesz?', sub: 'Wybierz swoją branżę.', required: true },
    { key: 'city', q: 'W jakim mieście działasz?', sub: 'Dzięki temu klienci z okolicy Cię znajdą.', required: true },
    { key: 'services', q: 'Wymień 2–3 usługi, które oferujesz', sub: 'Np. „Strzyżenie, Kąpiel, Trymowanie".', required: false },
    { key: 'photo', q: 'Dodaj logo lub zdjęcie firmy', sub: 'Możesz dodać teraz albo później w panelu.', required: false },
    { key: 'description', q: 'Opisz firmę w kilku słowach', sub: 'Albo pozwól, że napiszę za Ciebie.', required: false },
    { key: 'contact', q: 'Jak klienci mają się z Tobą kontaktować?', sub: 'Wystarczy telefon lub e-mail.', required: false },
  ],
};

const REASSURE_DONE = {
  hodowla: 'To dopiero początek! Po zalogowaniu w panelu dodasz swoje psy (reproduktory i suki) oraz mioty, wgrasz zdjęcia i opublikujesz stronę — wszystko w kilka chwil.',
  biznes: 'To dopiero początek! Po zalogowaniu w panelu dodasz usługi, portfolio i zdjęcia oraz opublikujesz swoją wizytówkę — wszystko w kilka chwil.',
};

const OWNER_DECLARATION = {
  hodowla: 'Oświadczam, że podane dane są prawdziwe, a hodowla należy do mnie lub mam upoważnienie, aby ją reprezentować.',
  biznes: 'Oświadczam, że podane dane są prawdziwe, a firma należy do mnie lub mam upoważnienie, aby ją reprezentować.',
};

const emptyForm = { name: '', breeds: [], city: '', organization: '', industrySlug: '', industryName: '', services: '', description: '', phone: '', email: '', coverDataUrl: '' };

function dataUrlToFile(dataUrl, filename) {
  const [head, b64] = dataUrl.split(',');
  const mime = (head.match(/:(.*?);/) || [])[1] || 'image/png';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

export default function StworzCreator() {
  const navigate = useNavigate();
  const persisted = (() => { try { return JSON.parse(localStorage.getItem('apis_creator_state') || 'null'); } catch (e) { return null; } })();
  const [type, setType] = useState(persisted?.type || null); // 'hodowla' | 'biznes'
  const [step, setStep] = useState(persisted?.step || 0);
  const [done, setDone] = useState(persisted?.done || false);
  const [form, setForm] = useState(persisted?.form ? { ...emptyForm, ...persisted.form } : emptyForm);
  const [query, setQuery] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [published, setPublished] = useState(null); // ścieżka opublikowanej strony (ekran „udostępnij")
  const fileRef = useRef(null);

  // Zapamiętaj postęp (bez dużego base64 zdjęcia), żeby odświeżenie/bezczynność nie cofały do startu.
  useEffect(() => {
    try {
      const { coverDataUrl, ...rest } = form;
      localStorage.setItem('apis_creator_state', JSON.stringify({ type, step, done, form: rest }));
    } catch (e) { /* localStorage niedostępny — ignoruj */ }
  }, [type, step, done, form]);

  const steps = type ? STEPS[type] : [];
  const current = steps[step];
  const total = steps.length;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const isStepValid = () => {
    if (!current) return false;
    if (!current.required) return true;
    switch (current.key) {
      case 'name': return form.name.trim().length > 1;
      case 'breeds': return form.breeds.length > 0;
      case 'city': return form.city.trim().length > 1;
      case 'industry': return !!form.industrySlug;
      default: return true;
    }
  };

  const goNext = () => {
    setQuery('');
    if (step < total - 1) setStep(step + 1);
    else setDone(true);
  };
  const goBack = () => {
    setQuery('');
    if (step > 0) setStep(step - 1);
  };

  const chooseType = (t) => { setType(t); setStep(0); setForm(emptyForm); setDone(false); };

  const writeForMe = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/assistant/public/generate-page-text`, {
        profile_type: type,
        name: form.name,
        city: form.city,
        specialty: type === 'hodowla' ? form.breeds.join(', ') : form.industryName,
        services: form.services,
      });
      if (data?.text) { set({ description: data.text }); toast.success('Gotowe! Możesz edytować tekst.'); }
    } catch (e) {
      toast.error('Nie udało się wygenerować opisu. Spróbuj ponownie.');
    } finally {
      setGenerating(false);
    }
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set({ coverDataUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const saveAndSignup = async () => {
    const email = signupEmail.trim().toLowerCase();
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) { toast.error('Podaj poprawny adres e-mail.'); return; }
    if (!consent) { toast.error('Potwierdź oświadczenie, aby opublikować stronę.'); return; }
    setSaving(true);
    const draft = {
      type, name: form.name, breeds: form.breeds, city: form.city, organization: form.organization,
      industryName: form.industryName, services: form.services, description: form.description,
      phone: form.phone, email: form.email,
    };
    try {
      const { data: auth } = await axios.post(`${API}/auth/soft-signup`, { email, name: form.name });
      const token = auth?.token;
      if (!token) throw new Error('no token');
      localStorage.setItem('token', token);
      const headers = { Authorization: `Bearer ${token}` };

      // Upload zdjęcia (jeśli dodane) → URL zapiszemy jako cover strony
      let coverUrl = '';
      if (form.coverDataUrl) {
        try {
          const ext = (form.coverDataUrl.match(/data:image\/(\w+)/) || [])[1] || 'png';
          const fd = new FormData();
          fd.append('file', dataUrlToFile(form.coverDataUrl, `cover.${ext}`));
          const { data: up } = await axios.post(`${API}/media-library/upload`, fd, { headers });
          coverUrl = up?.file_url || up?.thumbnail_url || '';
        } catch (e) { /* upload opcjonalny — kontynuuj bez zdjęcia */ }
      }

      const path = await provisionFromDraft(draft, token, coverUrl);
      localStorage.removeItem('apis_page_draft');
      localStorage.removeItem('apis_creator_state');
      toast.success('Twoja strona jest online! 🎉');
      setPublished(path); // ekran „udostępnij" zamiast natychmiastowego przekierowania
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        // Masz już konto z hasłem → zapisz draft i dokończ publikację PO zalogowaniu
        try { localStorage.setItem('apis_page_draft', JSON.stringify(draft)); } catch (_) { /* ignore */ }
        toast('Masz już konto — zaloguj się, a od razu opublikujemy Twoją stronę.');
        navigate('/logowanie?redirect=/stworz/dokoncz');
      } else {
        toast.error('Nie udało się zapisać. Spróbuj ponownie.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── TYPE SELECT ─────────────────────────────────────────────
  if (!type) {
    return (
      <div className="min-h-screen bg-[#0b1620] text-white flex items-center justify-center px-4" data-testid="stworz-type-select">
        <div className="max-w-3xl w-full text-center">
          <img src={MASCOT} alt="Asystent APIS" width="128" height="128" className="w-28 h-28 mx-auto mb-4 drop-shadow-[0_8px_24px_rgba(249,115,22,0.35)]" />
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold mb-4" style={{ color: ACCENT }}>
            <Sparkles className="w-4 h-4" /> Asystent APIS
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-4">Stwórz swoją stronę w 60 sekund</h1>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-xl mx-auto">
            Odpowiedz na kilka pytań, a przygotuję Ci gotową stronę. Nie martw się o szczegóły — resztę uzupełnisz później w panelu.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            <button data-testid="choose-hodowla" onClick={() => chooseType('hodowla')}
              className="group rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-8 text-left transition-colors">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(249,115,22,0.15)' }}>
                <Dog className="w-7 h-7" style={{ color: ACCENT }} />
              </div>
              <div className="text-xl font-bold mb-1">Jestem hodowcą</div>
              <p className="text-sm text-slate-400 mb-4">Strona hodowli, psy, mioty i zapytania od klientów.</p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: ACCENT }}>Zaczynam <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
            </button>
            <button data-testid="choose-biznes" onClick={() => chooseType('biznes')}
              className="group rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-8 text-left transition-colors">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(249,115,22,0.15)' }}>
                <Building2 className="w-7 h-7" style={{ color: ACCENT }} />
              </div>
              <div className="text-xl font-bold mb-1">Mam firmę</div>
              <p className="text-sm text-slate-400 mb-4">Wizytówka firmy, usługi, portfolio i zapytania.</p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: ACCENT }}>Zaczynam <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const shareUrl = published ? ((typeof window !== 'undefined' ? window.location.origin : 'https://apisai.pl') + published) : '';

  return (
    <div className="min-h-screen bg-[#0b1620] text-white" data-testid="stworz-creator">
      <div className="max-w-6xl mx-auto px-4 py-8 lg:py-12 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">

        {/* LEWA: pytania / ekran gotowe */}
        <div className="lg:sticky lg:top-12">
          {published ? (
            <div data-testid="stworz-published">
              <PartyPopper className="w-14 h-14 mb-4" style={{ color: ACCENT }} />
              <h2 className="text-3xl sm:text-4xl font-extrabold mb-3 leading-tight">Gotowe — Twoja strona jest online!</h2>
              <p className="text-base text-slate-300 mb-5">Udostępnij ją znajomym i klientom. Im więcej osób ją zobaczy, tym więcej zapytań otrzymasz.</p>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 mb-5 flex items-center gap-3" data-testid="stworz-published-url">
                <span className="text-xs sm:text-sm text-slate-300 truncate flex-1">{shareUrl}</span>
                <a href={published} className="text-xs font-bold whitespace-nowrap inline-flex items-center gap-1" style={{ color: ACCENT }} data-testid="stworz-open-page">
                  Otwórz <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>

              <p className="text-xs uppercase tracking-[0.18em] font-bold mb-3 text-slate-400">Udostępnij swoją stronę</p>
              <PageShareBar url={shareUrl} title={form.name || 'Moja strona w APISAI'} text={`Zobacz: ${form.name || 'moja strona w APISAI'}`} />

              <button
                data-testid="stworz-goto-page"
                onClick={() => { window.location.href = published; }}
                className="mt-7 inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full text-sm font-bold text-white transition-transform hover:-translate-y-0.5"
                style={{ background: ACCENT, boxShadow: '0 8px 24px rgba(249,115,22,0.35)' }}
              >
                Przejdź do mojej strony <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-slate-500 mt-4">Zarządzaj stroną, dodawaj psy/mioty i odbieraj zapytania w panelu — jesteś już zalogowany/a.</p>
            </div>
          ) : !done ? (
            <div data-testid="stworz-question">
              {/* progress */}
              <div className="flex items-center gap-3 mb-8">
                <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${((step + 1) / total) * 100}%`, background: ACCENT }} />
                </div>
                <span className="text-xs text-slate-400 font-medium tabular-nums">{step + 1}/{total}</span>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <img src={MASCOT} alt="" width="32" height="32" className="w-8 h-8" />
                <span className="text-xs uppercase tracking-[0.18em] font-bold" style={{ color: ACCENT }}>
                  {type === 'hodowla' ? 'Strona hodowli' : 'Wizytówka firmy'}
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 leading-tight">{current.q}</h2>
              <p className="text-sm text-slate-400 mb-6">{current.sub}</p>

              <div className="mb-6">{renderStepBody({ current, form, set, query, setQuery, generating, writeForMe, fileRef, onPhoto })}</div>

              <div className="flex items-center gap-3">
                {step > 0 && (
                  <button data-testid="stworz-back" onClick={goBack} className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full text-sm font-semibold text-slate-300 hover:text-white transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Wstecz
                  </button>
                )}
                <button data-testid="stworz-next" onClick={goNext} disabled={!isStepValid()}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
                  style={{ background: ACCENT, boxShadow: '0 8px 24px rgba(249,115,22,0.35)' }}>
                  {step === total - 1 ? 'Zobacz moją stronę' : 'Dalej'} <ArrowRight className="w-4 h-4" />
                </button>
                {!current.required && step < total - 1 && (
                  <button data-testid="stworz-skip" onClick={goNext} className="px-3 py-3 text-sm text-slate-400 hover:text-white transition-colors">Pomiń</button>
                )}
              </div>

              <div className="mt-8 flex items-start gap-2 text-xs text-slate-400 bg-white/[0.03] border border-white/10 rounded-xl p-3">
                <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: ACCENT }} />
                <span>Nie martw się o szczegóły — resztę uzupełnisz w panelu w każdej chwili.</span>
              </div>
            </div>
          ) : (
            <div data-testid="stworz-done">
              <img src={MASCOT} alt="Asystent APIS" width="96" height="96" className="w-24 h-24 mb-4 drop-shadow-[0_8px_24px_rgba(249,115,22,0.35)]" />
              <h2 className="text-3xl sm:text-4xl font-extrabold mb-3 leading-tight">Twoja strona jest gotowa! 🎉</h2>
              <p className="text-base text-slate-300 mb-6">Zobacz podgląd po prawej. Podaj e-mail, a opublikuję Twoją stronę online i przeniosę Cię prosto na nią.</p>

              <div className="rounded-2xl border p-5 mb-6" style={{ borderColor: 'rgba(249,115,22,0.4)', background: 'rgba(249,115,22,0.07)' }} data-testid="stworz-reassure-box">
                <div className="flex items-start gap-3">
                  <PawPrint className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: ACCENT }} />
                  <p className="text-sm text-orange-50/90 leading-relaxed">{REASSURE_DONE[type]}</p>
                </div>
              </div>

              <label className="block text-sm font-semibold text-slate-200 mb-2">Twój e-mail — zapiszemy na nim stronę</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input data-testid="stworz-signup-email" type="email" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)}
                  placeholder="np. kontakt@twojahodowla.pl"
                  className="flex-1 h-12 rounded-full px-5 bg-white/[0.05] border border-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-orange-400" />
                <button data-testid="stworz-save-btn" onClick={saveAndSignup} disabled={saving || !consent}
                  className="h-12 inline-flex items-center justify-center gap-2 px-7 rounded-full text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
                  style={{ background: ACCENT, boxShadow: '0 8px 24px rgba(249,115,22,0.35)' }}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Opublikuj moją stronę
                </button>
              </div>

              <label data-testid="stworz-consent" className="flex items-start gap-3 mt-4 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="stworz-consent-checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 w-5 h-5 flex-shrink-0 rounded border-white/25 bg-white/[0.05] accent-orange-500 cursor-pointer"
                />
                <span className="text-xs text-slate-400 leading-relaxed">
                  {OWNER_DECLARATION[type]} Akceptuję{' '}
                  <a href="/regulamin" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-200" onClick={(e) => e.stopPropagation()}>Regulamin</a>{' '}i{' '}
                  <a href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-200" onClick={(e) => e.stopPropagation()}>Politykę prywatności</a>{' '}oraz zgadzam się na publikację strony w katalogu APISAI.
                </span>
              </label>

              <p className="text-xs text-slate-500 mt-3">
                Masz już konto? <button onClick={() => navigate('/logowanie')} className="underline hover:text-slate-300">Zaloguj się</button>.
              </p>
            </div>
          )}
        </div>

        {/* PRAWA: podgląd na żywo */}
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold mb-3 text-slate-400 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: ACCENT }} /> Podgląd Twojej strony na żywo
          </div>
          <PreviewCard type={type} form={form} />
          <p className="text-xs text-slate-500 mt-3 text-center">Tak zobaczą Cię klienci w wyszukiwarce i katalogu ApisAI.</p>
        </div>
      </div>
    </div>
  );
}

// ── RENDER POJEDYNCZEGO PYTANIA ────────────────────────────────
function renderStepBody({ current, form, set, query, setQuery, generating, writeForMe, fileRef, onPhoto }) {
  const inputCls = 'w-full h-12 rounded-xl px-4 bg-white/[0.05] border border-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-orange-400 text-base';
  switch (current.key) {
    case 'name':
      return <input data-testid="field-name" className={inputCls} value={form.name} onChange={(e) => set({ name: e.target.value })}
        placeholder={form.name ? '' : 'np. Złote Marzenia FCI'} autoFocus />;
    case 'city':
      return <input data-testid="field-city" className={inputCls} value={form.city} onChange={(e) => set({ city: e.target.value })}
        placeholder="np. Warszawa" autoFocus />;
    case 'services':
      return <input data-testid="field-services" className={inputCls} value={form.services} onChange={(e) => set({ services: e.target.value })}
        placeholder="np. Strzyżenie, Kąpiel, Trymowanie" autoFocus />;
    case 'organization':
      return (
        <div className="flex flex-wrap gap-2" data-testid="field-organization">
          {ORG_OPTIONS.map((o) => {
            const active = form.organization === o;
            return (
              <button key={o} onClick={() => set({ organization: active ? '' : o })}
                className="px-4 py-2.5 rounded-full text-sm font-medium border transition-colors"
                style={active ? { background: ACCENT, borderColor: ACCENT, color: '#fff' } : { borderColor: 'rgba(255,255,255,0.15)', color: '#cbd5e1' }}>
                {o}
              </button>
            );
          })}
        </div>
      );
    case 'breeds': {
      const filtered = (query.trim() ? DOG_BREEDS.filter((b) => b.toLowerCase().includes(query.toLowerCase())) : DOG_BREEDS).slice(0, 8);
      const toggle = (b) => set({ breeds: form.breeds.includes(b) ? form.breeds.filter((x) => x !== b) : [...form.breeds, b] });
      return (
        <div data-testid="field-breeds">
          {form.breeds.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {form.breeds.map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-white" style={{ background: ACCENT }}>
                  {b} <button onClick={() => toggle(b)}><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input className={inputCls + ' pl-10'} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Szukaj rasy…" autoFocus />
          </div>
          <div className="flex flex-wrap gap-2">
            {filtered.map((b) => (
              <button key={b} onClick={() => toggle(b)}
                className="px-3 py-2 rounded-full text-sm border transition-colors"
                style={form.breeds.includes(b) ? { background: ACCENT, borderColor: ACCENT, color: '#fff' } : { borderColor: 'rgba(255,255,255,0.15)', color: '#cbd5e1' }}>
                {b}
              </button>
            ))}
          </div>
        </div>
      );
    }
    case 'industry': {
      const filtered = (query.trim() ? BUSINESS_INDUSTRIES.filter((i) => i.name.toLowerCase().includes(query.toLowerCase())) : BUSINESS_INDUSTRIES).slice(0, 10);
      return (
        <div data-testid="field-industry">
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input className={inputCls + ' pl-10'} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Szukaj branży… np. weterynarz" autoFocus />
          </div>
          <div className="flex flex-wrap gap-2">
            {filtered.map((i) => (
              <button key={i.slug} onClick={() => set({ industrySlug: i.slug, industryName: i.name })}
                className="px-3 py-2 rounded-full text-sm border transition-colors"
                style={form.industrySlug === i.slug ? { background: ACCENT, borderColor: ACCENT, color: '#fff' } : { borderColor: 'rgba(255,255,255,0.15)', color: '#cbd5e1' }}>
                {i.name}
              </button>
            ))}
          </div>
        </div>
      );
    }
    case 'photo':
      return (
        <div data-testid="field-photo">
          <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" />
          {form.coverDataUrl ? (
            <div className="relative rounded-xl overflow-hidden border border-white/15">
              <img src={form.coverDataUrl} alt="Podgląd" className="w-full h-44 object-cover" />
              <button onClick={() => { if (fileRef.current) fileRef.current.value = ''; set({ coverDataUrl: '' }); }}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()}
              className="w-full h-44 rounded-xl border-2 border-dashed border-white/20 hover:border-orange-400 flex flex-col items-center justify-center gap-2 text-slate-400 transition-colors">
              <ImagePlus className="w-7 h-7" />
              <span className="text-sm font-medium">Kliknij, aby dodać zdjęcie</span>
              <span className="text-xs">lub pomiń — dodasz je później w panelu</span>
            </button>
          )}
        </div>
      );
    case 'description':
      return (
        <div data-testid="field-description">
          <textarea className="w-full min-h-[130px] rounded-xl p-4 bg-white/[0.05] border border-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-orange-400 text-base leading-relaxed"
            value={form.description} onChange={(e) => set({ description: e.target.value })}
            placeholder="Napisz 2–3 zdania o sobie… albo kliknij Napisz za mnie" />
          <button data-testid="write-for-me" onClick={writeForMe} disabled={generating}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border transition-colors disabled:opacity-50"
            style={{ borderColor: ACCENT, color: ACCENT }}>
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Napisz za mnie
          </button>
        </div>
      );
    case 'contact':
      return (
        <div className="space-y-3" data-testid="field-contact">
          <div className="relative">
            <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input className={inputCls + ' pl-10'} value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="Telefon, np. +48 600 000 000" />
          </div>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input className={inputCls + ' pl-10'} value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="E-mail kontaktowy" />
          </div>
        </div>
      );
    default:
      return null;
  }
}

// ── PODGLĄD STRONY NA ŻYWO ─────────────────────────────────────
function slugifyName(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l').replace(/ /g, '-')
    .replace(/[^a-z0-9-]+/g, '').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
}

function PreviewCard({ type, form }) {
  const isKennel = type === 'hodowla';
  const title = form.name || (isKennel ? 'Nazwa Twojej hodowli' : 'Nazwa Twojej firmy');
  const tags = isKennel ? (form.breeds || []) : (form.industryName ? [form.industryName] : []);
  const subtitle = [tags.join(' · '), form.city].filter(Boolean).join(' • ');
  const services = (form.services || '').split(',').map((s) => s.trim()).filter(Boolean);
  const slug = slugifyName(form.name) || (isKennel ? 'twoja-hodowla' : 'twoja-firma');
  const url = `apisai.pl/${isKennel ? 'hodowla' : 'firma'}/${slug}`;
  const initial = (form.name || 'A').trim().charAt(0).toUpperCase();

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-white" data-testid="stworz-preview">
      {/* pasek przeglądarki */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[#0f1e2a] border-b border-white/10">
        <span className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        </span>
        <div className="flex-1 mx-1.5 h-6 rounded-md bg-white/[0.06] border border-white/10 flex items-center gap-1.5 px-2.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <span className="text-[11px] text-slate-400 truncate">{url}</span>
        </div>
      </div>

      {/* korpus strony (jasny — wygląda jak prawdziwa witryna) */}
      <div className="bg-white text-slate-900">
        {/* nav strony */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm font-extrabold flex-shrink-0" style={{ background: ACCENT }}>{initial}</span>
            <span className="font-bold text-sm text-slate-900 truncate">{title}</span>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-[12px] text-slate-500 flex-shrink-0">
            <span>{isKennel ? 'O hodowli' : 'O nas'}</span>
            <span>{isKennel ? 'Psy' : 'Oferta'}</span>
            <span>Kontakt</span>
          </div>
        </div>

        {/* hero */}
        <div className="relative h-48 flex items-end" style={{ background: form.coverDataUrl ? undefined : 'linear-gradient(135deg, #00273D 0%, #013a52 55%, #F97316 210%)' }}>
          {form.coverDataUrl && <img src={form.coverDataUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          {!form.coverDataUrl && <PawPrint className="w-20 h-20 text-white/10 absolute top-5 right-5" />}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.05) 60%)' }} />
          <div className="relative p-5">
            <h3 className="text-2xl font-extrabold text-white leading-tight drop-shadow">{title}</h3>
            {subtitle && (
              <span className="inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ background: 'rgba(249,115,22,0.9)' }}>{subtitle}</span>
            )}
          </div>
        </div>

        {/* treść */}
        <div className="p-5 space-y-5">
          {isKennel && form.organization && form.organization !== 'Jeszcze nie' && (
            <span className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700">{form.organization}</span>
          )}

          {/* O nas */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-1.5">{isKennel ? 'O hodowli' : 'O firmie'}</div>
            <p className={`text-sm leading-relaxed ${form.description ? 'text-slate-700' : 'text-slate-400 italic'}`}>
              {form.description || 'Tutaj pojawi się Twój opis…'}
            </p>
          </div>

          {/* rasy (hodowla) / usługi (firma) */}
          {isKennel && tags.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">Rasy</div>
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => <span key={t} className="px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100">{t}</span>)}
              </div>
            </div>
          )}
          {!isKennel && services.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">Usługi</div>
              <div className="flex flex-wrap gap-2">
                {services.map((s) => <span key={s} className="px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100">{s}</span>)}
              </div>
            </div>
          )}

          {/* galeria (placeholder — zdjęcia doda w panelu) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">{isKennel ? 'Galeria' : 'Realizacje'}</div>
              <span className="text-[10px] text-slate-400">Dodasz w panelu</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="aspect-square rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                  <ImagePlus className="w-5 h-5 text-slate-300" />
                </div>
              ))}
            </div>
          </div>

          {/* kontakt + CTA */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">Kontakt</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600 mb-3">
              {form.city && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" style={{ color: ACCENT }} /> {form.city}</span>}
              {form.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4" style={{ color: ACCENT }} /> {form.phone}</span>}
              {form.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4" style={{ color: ACCENT }} /> {form.email}</span>}
              {!form.city && !form.phone && !form.email && <span className="text-slate-400 italic">Dane kontaktowe uzupełnisz w panelu</span>}
            </div>
            <button className="w-full h-11 rounded-full text-sm font-bold text-white" style={{ background: ACCENT }} disabled>
              Wyślij zapytanie
            </button>
          </div>
        </div>

        {/* stopka */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" style={{ color: ACCENT }} /> Strona w ekosystemie APISAI</span>
          {form.city && <span>{form.city}</span>}
        </div>
      </div>
    </div>
  );
}
